import React, { useCallback, useState } from 'react';
import {
  getFormattedTime,
  isMiniscreen,
  MiniscreenMode,
  openWithMiniscreen,
} from '@lmt/unicorn-shared/app/components/patients/common';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';

import styles from './NightViewPanel.module.scss';
import { IconButton } from '../../common/ui-kit/IconButton';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { MiniscreenModeIcon } from '../../common/icons/miniscreen/MiniscreenModeIcon';
import clsx from 'clsx';

export interface NightViewInfo {
  id: number;
  room: string;
  roomId: number;
  startDate: Date;
  endDate: Date;
  patientId: number;
  patientName: string;
  subtenantCode: string;
  caseId: number;
  visitType: string;
}

export interface Props {
  currentRoom: number | null;
  visit: NightViewInfo;
  isStartDateHidden: boolean;
  hasPlaceColumn: boolean;
  openPatientTab: (patientId: number) => void;
  openCaseVisitTab: (
    patientId: number,
    caseId: number,
    visitId: number,
  ) => void;
  startMiniscreenOnline: boolean;
  startMiniscreenOffline: boolean;
}

export const NightViewItem: React.FC<Props> = (props) => {
  const {
    visit,
    hasPlaceColumn,
    currentRoom,
    openPatientTab,
    openCaseVisitTab,
  } = props;
  const [isLoading, setLoading] = useState<boolean>(false);
  const i18next = useScopedTranslation('patients.list.options');
  const startMeasuring = useCallback(
    async (mode: MiniscreenMode) => {
      setLoading(true);
      await openWithMiniscreen({ patientId: visit.patientId, mode });
      setLoading(false);
    },
    [visit.patientId],
  );

  return (
    <tr
      key={visit.id}
      onClick={() => openCaseVisitTab(visit.patientId, visit.caseId, visit.id)}
      data-test-id="night-view-item"
      className={clsx(currentRoom === visit.roomId && styles.currentRoom)}
    >
      {hasPlaceColumn && <td className={styles.place}>{visit.room}</td>}
      <td className={styles.timeRange}>
        {props.isStartDateHidden ? '-' : getFormattedTime(visit.startDate)}
      </td>
      <td
        className={styles.patientInfo}
        onClick={(e) => {
          e.stopPropagation();
          openPatientTab(visit.patientId);
        }}
      >
        <div className={styles.patientContainer}>
          <div className={styles.patient}>
            <div className={styles.patientName} title={visit.patientName}>
              {visit.patientName}
            </div>
          </div>
          <div className={styles.subtenantCode}>{visit.subtenantCode}</div>
        </div>
      </td>
      <td className={styles.visitType}>{visit.visitType}</td>
      {isMiniscreen() && (
        <td className={styles.controls} onClick={(e) => e.stopPropagation()}>
          {props.startMiniscreenOnline && (
            <IconButton
              disabled={!isMiniscreen() || isLoading}
              color={ButtonColor.Primary}
              onClick={() => startMeasuring(MiniscreenMode.Online)}
              className={styles.startButton}
              iconComponent={
                <MiniscreenModeIcon type="online" className={styles.icon} />
              }
              tooltip={i18next.t('start_miniscreen_online')}
            />
          )}
          {props.startMiniscreenOffline && (
            <IconButton
              disabled={!isMiniscreen() || isLoading}
              color={ButtonColor.Primary}
              onClick={() => startMeasuring(MiniscreenMode.Offline)}
              className={styles.startButton}
              iconComponent={
                <MiniscreenModeIcon type="offline" className={styles.icon} />
              }
              tooltip={i18next.t('start_miniscreen_offline')}
            />
          )}
        </td>
      )}
    </tr>
  );
};
